/** Every lap of the stint, one row each, read downwards.
 *
 * The colouring is the whole argument of the table: a sector or a lap time in
 * the accent is the best of the session, so a driver can see at a glance which
 * lap held which piece of the theoretical lap above. Everything else is
 * neutral, and the chosen row is brought forward with the accent bar and the
 * wash the design system gives a selected row — never by colouring its numbers,
 * which would collide with the one colour that means "best".
 *
 * The Sync column says something quieter than it looks. Every lap in this
 * table is by definition a lap the service holds, so each of them is synced;
 * what the column is really for is the row underneath it, which appears when
 * the session declares more laps than have arrived. Those laps are sitting in
 * the logger's queue and the portal cannot show them, so it says how many
 * there are rather than pretending the stint was shorter than it was. The one
 * other thing it says is "times only": the lap's recording has gone under the
 * lap retention, the row is still the lap's times, and there is nothing to
 * draw for it.
 *
 * The whole table scrolls sideways inside its wrapper rather than wrapping:
 * seven columns of figures at thirteen pixels do not fit a phone, and a lap
 * time broken over two lines is worse than one the reader has to scroll to.
 */

import { TBody, TD, TH, THead, TR, Table } from '@/components/ui';
import { DASH, delta, lapTime, sectorTime, shortDate } from '@/lib/format';
import { deltaToBestMs, isSectorBest } from '@/components/sessions/derive';
import type { LapTiming } from '@/lib/types';

export interface LapTableProps {
  laps: readonly LapTiming[];
  sectorBests: ReadonlyArray<number | null>;
  bestMs: number | null;
  selectedLap: number | null;
  onSelect: (lap: number) => void;
  /** Laps the session says were driven that the service has not received. */
  queued: number;
}

const ACCENT = 'var(--color-accent-300)';
const PLAIN = 'var(--color-neutral-300)';
const QUIET = 'var(--color-neutral-600)';

export function LapTable({
  laps,
  sectorBests,
  bestMs,
  selectedLap,
  onSelect,
  queued,
}: LapTableProps) {
  const width = sectorBests.length;
  const columns = width + 3;

  return (
    <Table
      scroll
      label="Laps"
      style={{ fontSize: 13, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
    >
      <THead>
        <TR>
          <TH>Lap</TH>
          {Array.from({ length: width }, (_unused, index) => (
            <TH key={index} numeric>{`S${index + 1}`}</TH>
          ))}
          <TH numeric>Time</TH>
          <TH numeric>Δ best</TH>
          <TH>Sync</TH>
        </TR>
      </THead>
      <TBody>
        {laps.map((lap) => {
          const selected = lap.lap === selectedLap;
          const isBest = bestMs !== null && lap.valid && lap.lap_time_ms === bestMs;
          const gap = deltaToBestMs(lap, bestMs);
          const timeColour = isBest ? ACCENT : lap.valid ? (selected ? 'var(--color-text)' : PLAIN) : QUIET;

          return (
            <TR key={lap.lap} selected={selected} onSelect={() => onSelect(lap.lap)}>
              <TD color="var(--color-neutral-500)">{lap.lap}</TD>
              {Array.from({ length: width }, (_unused, index) => {
                const ms = lap.sector_ms[index] ?? null;
                const best = lap.valid && isSectorBest(sectorBests, index, ms);
                return (
                  <TD
                    key={index}
                    numeric
                    color={best ? ACCENT : selected ? 'var(--color-text)' : PLAIN}
                  >
                    {sectorTime(ms)}
                  </TD>
                );
              })}
              <TD
                numeric
                color={timeColour}
                title={lap.valid ? undefined : 'not counted towards the best lap'}
              >
                {lapTime(lap.lap_time_ms)}
              </TD>
              <TD numeric color="var(--color-neutral-500)">
                {isBest ? 'best' : gap === null ? DASH : delta(gap)}
              </TD>
              <TD
                color={QUIET}
                style={{ fontSize: 11.5 }}
                title={
                  lap.stored === false
                    ? `the recording was removed${
                        lap.purged_at ? ` on ${shortDate(lap.purged_at)}` : ''
                      } under the lap retention; the times stay`
                    : undefined
                }
              >
                {lap.stored === false ? 'times only' : 'synced'}
              </TD>
            </TR>
          );
        })}

        {queued > 0 ? (
          <TR>
            <TD colSpan={columns} color="var(--color-warn)" style={{ fontSize: 11.5 }}>
              {queued === 1 ? '1 more lap is' : `${queued} more laps are`} queued on the logger and
              will appear once it can reach the service
            </TD>
          </TR>
        ) : null}
      </TBody>
    </Table>
  );
}

export default LapTable;
